// Integration Log Types

export type IntegrationLogStatus = 'success' | 'error' | 'warning';

export type IntegrationLogSource = 'financials' | 'dimensions' | 'xero' | 'quickbooks' | 'system';

export interface IntegrationLogEntry {
  id: string;
  timestamp: string;
  status: IntegrationLogStatus;
  source: IntegrationLogSource;
  transactionId?: string;
  transactionType?: string;
  assetId?: string;
  assetCode?: string;
  errorCode?: string;
  message: string;
  details?: string;
  userId?: string;
  userName?: string;
  resolved: boolean;
  resolvedAt?: string;
  resolvedBy?: string;
  notes?: string;
}

export interface IntegrationLogFilters {
  status?: IntegrationLogStatus[];
  source?: IntegrationLogSource[];
  dateFrom?: string;
  dateTo?: string;
  resolved?: boolean;
  search?: string;
}

export interface IntegrationLogResponse {
  data: IntegrationLogEntry[];
  total: number;
  page: number;
  pageSize: number;
}

// Mock data for development
export const mockIntegrationLogs: IntegrationLogEntry[] = [
  {
    id: 'log-001',
    timestamp: '2024-01-15T14:30:00Z',
    status: 'error',
    source: 'financials',
    transactionId: 'TXN-2024-001',
    transactionType: 'Addition',
    assetId: 'AST-00123',
    assetCode: 'LAP-2024-001',
    errorCode: 'FIN-001',
    message: 'Failed to create journal entry in Access Financials',
    details: 'Connection timeout while attempting to post transaction to financial system. The asset was created successfully in Asset Manager but the corresponding journal entry was not created.',
    userId: 'user-001',
    userName: 'John Smith',
    resolved: false,
  },
  {
    id: 'log-002',
    timestamp: '2024-01-15T13:15:00Z',
    status: 'warning',
    source: 'financials',
    transactionId: 'TXN-2024-002',
    transactionType: 'Depreciation',
    assetId: 'AST-00124',
    assetCode: 'VEH-2024-001',
    errorCode: 'FIN-002',
    message: 'Cost center not found in financial system',
    details: 'The cost center "CC-999" specified in the transaction does not exist in Access Financials. The transaction was created with a default cost center.',
    userId: 'user-002',
    userName: 'Sarah Johnson',
    resolved: true,
    resolvedAt: '2024-01-15T14:00:00Z',
    resolvedBy: 'admin-001',
    notes: 'Cost center was created in financial system and transaction was reprocessed.',
  },
  {
    id: 'log-003',
    timestamp: '2024-01-15T12:45:00Z',
    status: 'error',
    source: 'dimensions',
    transactionId: 'TXN-2024-003',
    transactionType: 'Transfer',
    assetId: 'AST-00125',
    assetCode: 'FUR-2024-001',
    errorCode: 'DIM-001',
    message: 'Invalid dimension mapping',
    details: 'The location dimension "LOC-NORTH" could not be mapped to a valid dimension in Access Dimensions. Transaction requires manual review.',
    userId: 'user-003',
    userName: 'Mike Wilson',
    resolved: false,
  },
  {
    id: 'log-004',
    timestamp: '2024-01-15T11:20:00Z',
    status: 'warning',
    source: 'financials',
    transactionId: 'TXN-2024-004',
    transactionType: 'Revaluation',
    assetId: 'AST-00126',
    assetCode: 'MAC-2024-001',
    errorCode: 'FIN-003',
    message: 'Revaluation amount exceeds threshold',
    details: 'The revaluation amount of £50,000 exceeds the configured threshold of £25,000. Transaction requires approval before posting to financial system.',
    userId: 'user-001',
    userName: 'John Smith',
    resolved: true,
    resolvedAt: '2024-01-15T11:45:00Z',
    resolvedBy: 'manager-001',
    notes: 'Approved by finance manager. Transaction posted successfully.',
  },
  {
    id: 'log-005',
    timestamp: '2024-01-15T10:30:00Z',
    status: 'error',
    source: 'financials',
    transactionId: 'TXN-2024-005',
    transactionType: 'Disposal',
    assetId: 'AST-00127',
    assetCode: 'LAP-2023-045',
    errorCode: 'FIN-004',
    message: 'Asset not found in financial system',
    details: 'The asset "LAP-2023-045" exists in Asset Manager but could not be found in the financial system. This may indicate a synchronization issue.',
    userId: 'user-004',
    userName: 'Emma Davis',
    resolved: false,
  },
  {
    id: 'log-006',
    timestamp: '2024-01-15T09:15:00Z',
    status: 'warning',
    source: 'system',
    errorCode: 'SYS-001',
    message: 'Batch processing delayed',
    details: 'The scheduled batch process for posting transactions to the financial system was delayed by 15 minutes due to high system load.',
    resolved: true,
    resolvedAt: '2024-01-15T09:30:00Z',
    resolvedBy: 'system',
    notes: 'Batch process completed successfully after delay.',
  },
  {
    id: 'log-007',
    timestamp: '2024-01-14T16:45:00Z',
    status: 'error',
    source: 'financials',
    transactionId: 'TXN-2024-006',
    transactionType: 'Addition',
    assetId: 'AST-00128',
    assetCode: 'IT-2024-012',
    errorCode: 'FIN-005',
    message: 'Duplicate transaction detected',
    details: 'A transaction with the same reference number already exists in the financial system. This may be a duplicate submission.',
    userId: 'user-002',
    userName: 'Sarah Johnson',
    resolved: true,
    resolvedAt: '2024-01-14T17:00:00Z',
    resolvedBy: 'user-002',
    notes: 'Confirmed as duplicate. Original transaction retained.',
  },
  {
    id: 'log-008',
    timestamp: '2024-01-14T15:30:00Z',
    status: 'warning',
    source: 'dimensions',
    transactionId: 'TXN-2024-007',
    transactionType: 'Depreciation',
    assetId: 'AST-00129',
    assetCode: 'VEH-2023-089',
    errorCode: 'DIM-002',
    message: 'Dimension value truncated',
    details: 'The project code "PROJ-INFRASTRUCTURE-UPGRADE-2024" exceeded the maximum length and was truncated to "PROJ-INFRASTRUCTURE-UPGR".',
    userId: 'user-003',
    userName: 'Mike Wilson',
    resolved: true,
    resolvedAt: '2024-01-14T15:45:00Z',
    resolvedBy: 'user-003',
    notes: 'Updated project code to use shorter format.',
  },
];
